import React, { useMemo } from "react";

/**
 * Sunucu taraflı sayfalama şeridi: "Toplam N kayıt" + sayfa numaraları + sayfa boyutu.
 *
 * Sunum bileşenidir; veriyi çağıran ekran getirir. Sayfa numaraları 0 tabanlıdır
 * (backend PageResponse ile aynı), ekranda 1 tabanlı gösterilir.
 *
 * Not: Kullanıcılar/Ödeme Geçmişi/Vade Takip ekranları kendi yerel şeritlerini taşıyor;
 * bu bileşen yeni ekranlar için ortak sürümdür, eskiler zamanla buraya taşınabilir.
 *
 * @param currentPage      0 tabanlı aktif sayfa
 * @param totalPages       toplam sayfa sayısı
 * @param totalElements    toplam kayıt sayısı (0 ise şerit hiç çizilmez)
 * @param pageSize         sayfa başına kayıt
 * @param onPageChange     (page) => void
 * @param onPageSizeChange (size) => void
 * @param pageSizeOptions  seçilebilir sayfa boyutları
 */
const PaginationBar = ({
    currentPage,
    totalPages,
    totalElements,
    pageSize,
    onPageChange,
    onPageSizeChange,
    pageSizeOptions = [10, 25, 50, 100],
}) => {
    // Kaydırmalı pencere: sayfa sayısı büyüdüğünde şerit taşmasın, aktif sayfa ortada kalsın.
    const pages = useMemo(() => {
        const arr = [];
        const max = 5;
        let start = Math.max(0, currentPage - Math.floor(max / 2));
        const end = Math.min(totalPages - 1, start + max - 1);
        if (end - start + 1 < max) start = Math.max(0, end - max + 1);
        for (let i = start; i <= end; i++) arr.push(i);
        return arr;
    }, [currentPage, totalPages]);

    if (!totalElements) return null;

    return (
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mt-3">
            <div className="text-muted" style={{ fontSize: ".9rem" }}>
                Toplam <strong>{totalElements}</strong> kayıt
            </div>

            <div className="d-flex align-items-center gap-3 flex-wrap justify-content-end">
                <nav>
                    <ul className="pagination pagination-sm mb-0 corporate-pagination">
                        <li className={`page-item ${currentPage === 0 ? "disabled" : ""}`}>
                            <button className="page-link" onClick={() => onPageChange(currentPage - 1)}
                                disabled={currentPage === 0} aria-label="Önceki sayfa">
                                <i className="bx bx-chevron-left" />
                            </button>
                        </li>
                        {pages.map((p) => (
                            <li key={p} className={`page-item ${currentPage === p ? "active" : ""}`}>
                                <button className="page-link" onClick={() => onPageChange(p)}>{p + 1}</button>
                            </li>
                        ))}
                        <li className={`page-item ${currentPage >= totalPages - 1 ? "disabled" : ""}`}>
                            <button className="page-link" onClick={() => onPageChange(currentPage + 1)}
                                disabled={currentPage >= totalPages - 1} aria-label="Sonraki sayfa">
                                <i className="bx bx-chevron-right" />
                            </button>
                        </li>
                    </ul>
                </nav>

                <div className="d-flex align-items-center gap-2">
                    <span className="text-muted" style={{ fontSize: ".9rem" }}>Sayfa başına</span>
                    <select className="form-select form-select-sm rounded-pill" style={{ width: 80 }}
                        value={pageSize} onChange={(e) => onPageSizeChange(parseInt(e.target.value, 10))}>
                        {pageSizeOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                </div>
            </div>
        </div>
    );
};

export default PaginationBar;
